import { type ReactNode } from 'react'
import { LifeBuoy } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { formatDateHuman } from '@/lib/utils'
import type { PromoCode } from '@/types'

/** Read-only details for a manager-issued recovery coupon. Opened from the
 *  Promo codes → Active codes table by clicking the recovery row's label.
 *  Everything shown comes from the `recovery` block the list endpoint joins
 *  in (reason / tier / manager / customer / order) — no extra fetch. */
export function RecoveryCouponDetailsDialog({
  promo,
  open,
  onOpenChange,
}: {
  promo: PromoCode | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const r = promo?.recovery ?? null
  const pct = promo ? String(Number(promo.discount_value.toFixed(2))) : ''
  const orderRef = r?.order_number
    ? `#${r.order_number}`
    : r?.order_id
      ? r.order_id.slice(0, 8)
      : '—'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <LifeBuoy className="h-4 w-4" aria-hidden />
            </span>
            Recovery coupon
          </DialogTitle>
          <DialogDescription>
            A manager issued this for a guest's next visit. Code{' '}
            <span className="font-mono font-semibold text-foreground">{promo?.code}</span>.
          </DialogDescription>
        </DialogHeader>

        {r ? (
          <div className="divide-y divide-border/60">
            <DetailRow label="Customer">
              <span className="font-medium">{r.customer_name || r.customer_phone || 'Guest'}</span>
              {r.customer_name && r.customer_phone ? (
                <span className="block text-sm font-normal text-muted-foreground">{r.customer_phone}</span>
              ) : null}
            </DetailRow>
            <DetailRow label="Severity">
              <span className="inline-flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="border-amber-400/60 text-amber-700 dark:text-amber-300"
                >
                  {titleCase(r.tier)}
                </Badge>
                <span>{pct}% off</span>
              </span>
            </DetailRow>
            <DetailRow label="Reason">{r.reason || '—'}</DetailRow>
            <DetailRow label="Issued by">
              <span className="font-medium">{r.issued_by_name || 'Manager'}</span>
              <span className="block text-sm font-normal text-muted-foreground">
                {formatDateHuman(r.issued_at)}
              </span>
            </DetailRow>
            <DetailRow label="Order">{orderRef}</DetailRow>
            <DetailRow label="Valid until">
              {promo?.valid_to ? formatDateHuman(promo.valid_to) : '—'}
            </DetailRow>
          </div>
        ) : (
          <p className="py-4 text-sm text-muted-foreground">No recovery details available.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

function titleCase(s: string): string {
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="shrink-0 text-sm font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-right text-sm text-foreground">{children}</span>
    </div>
  )
}
